import React from 'react';
import { Box, Button, ButtonBase, Skeleton, Typography } from '@mui/material';
import { inboxChip } from './todayModel';
import { groupByUrgency } from '../inbox/inboxModel';
import { nextSessionLabel } from '../schedule';
import { dueLabel } from '../tasks/deriveTasks';
import { ClientWork, DashboardClient } from '../tasks/types';
import { BAND_LABELS, DueTag, INK, panelSx, TONES } from '../ui';

const PER_BAND = 5;

/**
 * Clients with something due whom the coach won't see that day. Each row
 * opens the client in the Inbox, where the work can be done in place.
 */
export const WaitingRail: React.FC<{
  work: ClientWork[];
  loading: boolean;
  now: Date;
  dayLabel: string;
  /** Overrides the client list's "Next session" when the coach's Mindbody schedule knows better. */
  nextLabel?: (client: DashboardClient) => string;
  onOpen: (clientId: string) => void;
  onSeeAll: () => void;
}> = ({ work, loading, now, dayLabel, nextLabel, onOpen, onSeeAll }) => (
  <Box component="section" aria-labelledby="waiting-heading">
    <Typography id="waiting-heading" component="h2" sx={{ fontSize: 15, fontWeight: 600 }}>
      Not on {dayLabel}'s schedule
    </Typography>
    <Typography sx={{ fontSize: 13, color: INK.muted, mt: 0.25, mb: 2 }}>Clients with something due whom you won't see {dayLabel}.</Typography>

    {loading ? (
      <Box sx={{ ...panelSx, px: 2, py: 1 }} aria-busy="true">
        {[0, 1, 2, 3].map((i) => (
          <Box key={i} sx={{ py: 1 }}>
            <Skeleton width="55%" height={16} />
            <Skeleton width="38%" height={14} />
          </Box>
        ))}
      </Box>
    ) : work.length === 0 ? (
      <Typography sx={{ ...panelSx, px: 2, py: 2, fontSize: 14, color: INK.body }}>Nobody else is waiting on you.</Typography>
    ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {groupByUrgency(work).map(({ urgency, items }) => (
          <Box key={urgency}>
            <Typography component="h3" sx={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: TONES[urgency].color, mb: 1 }}>
              {BAND_LABELS[urgency]} · {items.length}
            </Typography>
            <Box component="ul" sx={{ ...panelSx, listStyle: 'none', m: 0, px: 2, py: 0 }}>
              {items.slice(0, PER_BAND).map((item) => (
                <Box component="li" key={item.client.clientId} sx={{ '& + &': { borderTop: `1px solid ${INK.faint}` } }}>
                  <ButtonBase
                    onClick={() => onOpen(item.client.clientId)}
                    sx={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.5, textAlign: 'left', justifyContent: 'flex-start' }}
                  >
                    <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                        {item.client.name}{' '}
                        {/* Only Ignition and Foundation: other clients' programme field is a pricing option name. */}
                        {item.client.programme ? (
                          <Box component="span" sx={{ fontWeight: 400, fontSize: 12, color: INK.muted }}>
                            {item.client.programmeLabel}
                          </Box>
                        ) : null}
                      </Typography>
                      <Typography sx={{ fontSize: 14, color: INK.body }}>{inboxChip(item)}</Typography>
                      <Typography sx={{ fontSize: 12, color: INK.muted, mt: 0.25 }}>Next session: {nextLabel ? nextLabel(item.client) : nextSessionLabel(item.client, now)}
                      </Typography>
                    </Box>
                    <DueTag urgency={item.urgency}>{dueLabel(item.tasks[0], now)}</DueTag>
                  </ButtonBase>
                </Box>
              ))}
            </Box>
            {items.length > PER_BAND ? (
              <Button size="small" onClick={onSeeAll} sx={{ mt: 0.5, textTransform: 'none', fontWeight: 600 }}>
                {items.length - PER_BAND} more in Actions
              </Button>
            ) : null}
          </Box>
        ))}
      </Box>
    )}
  </Box>
);
